import { css } from 'astroturf';
import React, { useEffect, useRef, useState } from 'react';
import Prism from 'prismjs';

const styles = css`
  .examplecontainer {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 8px;
    border-radius: 8px;
    overflow: hidden;
    background: #f7faff;
    margin: 2rem 0;
    padding: 2rem;
    background: linear-gradient(135deg, rgba(103, 178, 111, 0.25), ease-in-out, rgba(60, 176, 255, 0.25));
    border-radius: 16px;
    box-shadow: rgba(11, 66, 124, 0.267) 0.3em 0.3em 2em inset;

    @media (max-width: 40rem) {
      grid-template-columns: 1fr;
      grid-template-rows: repeat(2, minmax(200px, 1fr));
    }
  }

  .code {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 8px;

    & pre {
      margin: 0 !important;
      padding: 0 !important;
      border-radius: 0 !important;
      background: transparent !important;
    }

    & > div > div {
      margin: 0 !important;
      width: 100%;
    }

    & .block {
      position: relative;
      overflow: hidden;
      max-height: 20rem;
      display: block;
      border-radius: 8px;
      padding: 1rem;
      background: #1e1e3f;
      color: #fff;

      & > div {
        overflow: visible;
      }

      & > div,
      & textarea {
        width: calc(100% - 2rem);
        height: calc(100% - 2rem);
        position: absolute;
        inset: 1rem;
      }
    }

    & textarea {
      border: 0;
      z-index: 2;
      background: none;
      color: transparent;
      resize: none;
      direction: ltr;
      text-align: left;
      overflow: auto;
      white-space: nowrap;
      word-spacing: normal;
      word-break: normal;
      tab-size: 4;
      hyphens: none;
      font-family: 'Operator Mono', 'Fira Code', Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
      font-weight: 400;
      font-size: 1rem;
      line-height: 1.5rem;
      letter-spacing: 0.5px;
      outline: none;
      caret-color: #fff;
    }

    & textarea::selection {
      background: #fff;
      color: #000;
    }
  }

  .block::before,
  .result::before {
    position: absolute;
    top: 0;
    z-index: 1;
    padding: 0.125rem 0.5rem 0 0.25rem;
    background: #1e1e3f;
    color: #fff;
    font-size: 0.6rem;
    line-height: 1.25;
    font-weight: 500;
    text-transform: uppercase;
    border: 2px solid #f7faff;
    right: 0;
    border-radius: 0 0 0 0.25rem;
    border-width: 0 0 2px 2px;
    pointer-events: none;
  }

  .block:global(:has(pre.language-html))::before {
    content: 'html';
  }

  .block:global(:has(pre.language-css))::before {
    content: 'css';
    top: 2px;
  }

  .result {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    background-color: #fff;
    min-height: 200px;

    & iframe {
      background-color: #fff;
      border: 0;
      width: 100%;
      height: 100%;
      position: absolute;
      inset: 0;
    }

    &::before {
      z-index: 1;
      content: 'result';
    }
  }
`;

const Editor = ({ html, cssCode, setHtml, setCSS, htmlHeight, cssHeight }) => {
  const iframe = useRef(null);
  const [htmlOffset, setHtmlOffset] = useState({ x: 0, y: 0 });
  const [cssOffset, setCssOffset] = useState({ x: 0, y: 0 });

  const combination = `<!DOCTYPE html>
<html>
<head>
<style>
body { margin: 1rem; font-family: system-ui; }
${cssCode}
</style>
</head>
<body>
${html}
</body>
</html>`;

  useEffect(() => {
    Prism.highlightAll();
    if (iframe.current) {
      iframe.current.srcdoc = combination;
    }
  }, [cssCode, html, combination]);

  return (
    <div className={styles.examplecontainer}>
      <div className={styles.code}>
        <div className={styles.block} style={{ height: htmlHeight }}>
          <div className="gatsby-highlight" style={{ translate: `-${htmlOffset.x}px -${htmlOffset.y}px` }}>
            <pre className="language-html">
              <code>{html}</code>
            </pre>
          </div>
          <textarea
            spellCheck={false}
            value={html}
            onScroll={(e) => setHtmlOffset({ x: e.target.scrollLeft, y: e.target.scrollTop })}
            onChange={(e) => setHtml(e.target.value)}
          />
        </div>
        <div className={styles.block} style={{ height: cssHeight }}>
          <div className="gatsby-highlight" style={{ translate: `-${cssOffset.x}px -${cssOffset.y}px` }}>
            <pre className="language-css">
              <code>{cssCode}</code>
            </pre>
          </div>
          <textarea
            spellCheck={false}
            value={cssCode}
            onScroll={(e) => setCssOffset({ x: e.target.scrollLeft, y: e.target.scrollTop })}
            onChange={(e) => setCSS(e.target.value)}
          />
        </div>
      </div>
      <div className={styles.result}>
        <iframe ref={iframe} title="result" />
      </div>
    </div>
  );
};

export const AnonymousBoxExample = () => {
  const [html, setHtml] = useState(`<div>
  <div class="half">First</div>
  <div class="half">Second</div>
</div>`);
  const [cssCode, setCSS] = useState(`.half {
    display: inline-block;
    width: 50%;

    background: hotpink;
}
.half + .half {
    background: dodgerblue;
}`);

  return (
    <Editor html={html} cssCode={cssCode} setHtml={setHtml} setCSS={setCSS} htmlHeight="140px" cssHeight="250px" />
  );
};

export const BoxModelExample = () => {
  const [html, setHtml] = useState(`<div class="content-box">Content Box</div>
<div class="border-box">Border Box</div>`);
  const [cssCode, setCSS] = useState(`div {
  width: 200px;
  padding: 20px;
  margin-bottom: 1rem;
}
.content-box {
  box-sizing: content-box;
  border: 5px solid blue;
  background: lightblue;
}
.border-box {
  box-sizing: border-box;
  border: 5px solid green;
  background: lightgreen;
}`);

  return (
    <Editor html={html} cssCode={cssCode} setHtml={setHtml} setCSS={setCSS} htmlHeight="100px" cssHeight="440px" />
  );
};

export const InlineBlockExample = () => {
  const [html, setHtml] = useState(`<p>This is <span class="inline">inline</span> text.</p>
<p>This is <span class="block">block</span> text.</p>
<div class="inline-block">Inline-Block 1</div>
<div class="inline-block">Inline-Block 2</div>`);
  const [cssCode, setCSS] = useState(`.inline {
  background: yellow;
  padding: 5px;
  margin: 10px;
}

.block {
  display: block;
  background: lightblue;
  padding: 5px;
  margin: 10px;
}

.inline-block {
  display: inline-block;
  background: lightgreen;
  padding: 10px;
  margin: 10px;
}`);

  return (
    <Editor html={html} cssCode={cssCode} setHtml={setHtml} setCSS={setCSS} htmlHeight="180px" cssHeight="280px" />
  );
};

export const BaselineExample = () => {
  const [html, setHtml] = useState(`<div class="line">
  Text <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50'%3E%3Crect fill='blue' width='50' height='50'/%3E%3C/svg%3E" alt="box">
</div>`);
  const [cssCode, setCSS] = useState(`.line {
  line-height: 2;
  background: lightgray;
}

img {
  /* Try: vertical-align: bottom; */
}`);

  return (
    <Editor html={html} cssCode={cssCode} setHtml={setHtml} setCSS={setCSS} htmlHeight="120px" cssHeight="150px" />
  );
};

export const MarginCollapseExample = () => {
  const [html, setHtml] = useState(`<div class="box1">Box 1 (margin-bottom: 30px)</div>
<div class="box2">Box 2 (margin-top: 20px)</div>`);
  const [cssCode, setCSS] = useState(`.box1 {
  background: lightblue;
  padding: 10px;
  margin-bottom: 30px;
}

.box2 {
  background: lightgreen;
  padding: 10px;
  margin-top: 20px;
}

/* The actual space between is 30px, not 50px */`);

  return (
    <Editor html={html} cssCode={cssCode} setHtml={setHtml} setCSS={setCSS} htmlHeight="100px" cssHeight="220px" />
  );
};

export const MarginAutoExample = () => {
  const [html, setHtml] = useState(`<div class="centered">Centered with margin: auto</div>`);
  const [cssCode, setCSS] = useState(`.centered {
  width: 200px;
  margin: 20px auto;
  padding: 20px;
  background: lightblue;
  border: 2px solid blue;
}`);

  return <Editor html={html} cssCode={cssCode} setHtml={setHtml} setCSS={setCSS} htmlHeight="80px" cssHeight="150px" />;
};

export const PositionRelativeExample = () => {
  const [html, setHtml] = useState(`<div class="box">Normal Position</div>
<div class="box relative">Relative Position</div>
<div class="box">Normal Position</div>`);
  const [cssCode, setCSS] = useState(`.box {
  background: lightblue;
  padding: 10px;
  margin: 10px 0;
  border: 2px solid blue;
}

.relative {
  position: relative;
  top: 20px;
  left: 40px;
  background: lightcoral;
  border-color: red;
}`);

  return (
    <Editor html={html} cssCode={cssCode} setHtml={setHtml} setCSS={setCSS} htmlHeight="140px" cssHeight="220px" />
  );
};

export const PositionAbsoluteExample = () => {
  const [html, setHtml] = useState(`<div class="container">
  <div class="absolute">
    Absolute positioned
  </div>
  Normal content in container
</div>`);
  const [cssCode, setCSS] = useState(`.container {
  position: relative;
  min-height: 150px;
  padding: 10px;
  background: lightgray;
  border: 2px solid gray;
}

.absolute {
  position: absolute;
  top: 10px;
  right: 10px;
  background: lightcoral;
  padding: 10px;
  border: 2px solid red;
}`);

  return (
    <Editor html={html} cssCode={cssCode} setHtml={setHtml} setCSS={setCSS} htmlHeight="140px" cssHeight="260px" />
  );
};

export const StackingContextExample = () => {
  const [html, setHtml] = useState(`<div class="container">
  <div class="box1">Box 1 (z-index: 1)</div>
  <div class="box2">Box 2 (z-index: 2)</div>
  <div class="box3">Box 3 (z-index: 3)</div>
</div>`);
  const [cssCode, setCSS] = useState(`.container {
  position: relative;
  height: 200px;
}

.box1, .box2, .box3 {
  position: absolute;
  padding: 20px;
  width: 150px;
}

.box1 {
  background: lightblue;
  top: 20px;
  left: 20px;
  z-index: 1;
}

.box2 {
  background: lightgreen;
  top: 60px;
  left: 60px;
  z-index: 2;
}

.box3 {
  background: lightcoral;
  top: 100px;
  left: 100px;
  z-index: 3;
}`);

  return (
    <Editor html={html} cssCode={cssCode} setHtml={setHtml} setCSS={setCSS} htmlHeight="160px" cssHeight="380px" />
  );
};

export const FlexExample = () => {
  const [html, setHtml] = useState(`<div class="flex-container">
  <div class="item">Item 1</div>
  <div class="item">Item 2</div>
  <div class="item">Item 3</div>
</div>`);
  const [cssCode, setCSS] = useState(`.flex-container {
  display: flex;
  gap: 10px;
  background: lightgray;
  padding: 10px;
}

.item {
  background: lightblue;
  padding: 20px;
  border: 2px solid blue;
}`);

  return (
    <Editor html={html} cssCode={cssCode} setHtml={setHtml} setCSS={setCSS} htmlHeight="140px" cssHeight="200px" />
  );
};

export const GridExample = () => {
  const [html, setHtml] = useState(`<div class="grid-container">
  <div class="item">Item 1</div>
  <div class="item">Item 2</div>
  <div class="item">Item 3</div>
  <div class="item">Item 4</div>
</div>`);
  const [cssCode, setCSS] = useState(`.grid-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  background: lightgray;
  padding: 10px;
}

.item {
  background: lightgreen;
  padding: 20px;
  border: 2px solid green;
}`);

  return (
    <Editor html={html} cssCode={cssCode} setHtml={setHtml} setCSS={setCSS} htmlHeight="160px" cssHeight="220px" />
  );
};

export const WhitespaceSolutionsExample = () => {
  const [html, setHtml] = useState(`

<!-- Solution 1: No whitespace in DOM -->
<div class="container no-whitespace" data- label="Remove the whitespace in the DOM">
  <div class="half">50%</div><div class="half">50%</div>
</div>

<!-- Solution 2: white-space: nowrap -->
<div class="container nowrap" label="white-space: nowrap">
  <div class="half">50%</div>
  <div class="half">50%</div>
</div>

<!-- Solution 3: font-size: 0 -->
<div class="container font-zero" label="font-size: 0 on parent">
  <div class="half">50%</div>
  <div class="half">50%</div>
</div>

<!-- Solution 4: white-space-collapse: discard  -->
<div class="container collapse" label="white-space-collapse: collapse">
  <div class="half">50%</div>
  <div class="half">50%</div>
</div>`);
  const [cssCode, setCSS] = useState(`.container {
  margin-bottom: 1rem;
  background: lightgray;
}

.container::before {
  content: attr(label);
  display: block;
  font-size: 0.75rem;
  padding: 0.5rem;
}

.half {
  display: inline-block;
  width: 50%;
  background: lightblue;
  border: 2px solid blue;
  box-sizing: border-box;
}

/* Solution 2: white-space: nowrap */
.nowrap {
  white-space: nowrap;
}

/* Solution 3: font-size: 0 on parent */
.font-zero {
  font-size: 0;
}

.font-zero .half {
  font-size: 1rem; /* Reset for children */
}

/* Solution 4: white-space-collapse */
.collapse {
  white-space-collapse: discard;
}`);

  return (
    <Editor html={html} cssCode={cssCode} setHtml={setHtml} setCSS={setCSS} htmlHeight="460px" cssHeight="420px" />
  );
};
